import {
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  Chip,
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Checkbox,
} from '@mui/material';
import { ActionIconButton } from 'components/button/ActionIconButton';
import {
  ResultPanel,
  RunButton,
  RunRow,
  TestAccordion,
  TestAccordionDetails,
  TestAccordionSummary,
  TestActionBar,
  TestCard,
} from './PromptsTestCaseItem.styles';
import { FONT_MONO } from 'themes/tokens';
import { PromptTestCase, PromptTestCaseRequest, PromptTestCasePreview, Prompt, PromptTestCaseRun } from 'api/model';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RefreshIcon from '@mui/icons-material/Refresh';
import useDialog from 'hooks/useDialog';
import CloseButton from 'components/button/CloseButton';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import usePromptTestCase from 'api/prompts-test-cases/usePromptTestCase';
import { useCallback, useMemo, useState } from 'react';
import templates from 'components/overwrite/FormTemplate';
import { getFormSchema, getUiSchema } from 'api/prompts-test-cases/formSchema';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { Form } from '@rjsf/mui';
import useModels from 'api/prompts-models/useModels';
import Loader from 'components/Loader';
import TextDiff from 'components/diff/TextDiff';

const RunStat = ({ label, value }: { label: string; value: string | number }) => (
  <div className="flex min-w-0 flex-col">
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: FONT_MONO }} noWrap>
      {value}
    </Typography>
  </div>
);

type Props = {
  prompt?: Prompt;
  testCase: PromptTestCase;
};

type PromptTestCaseFormData = PromptTestCaseRequest & {
  run_count?: number;
};

const PromptsTestCaseItem = ({ prompt, testCase }: Props) => {
  const { models, isLoading: isModelsLoading } = useModels();
  const { remove, update, execute, getPreview, runs, areRunsLoading } = usePromptTestCase(
    prompt?.id ?? '',
    testCase.id,
    prompt?.revision ?? 0,
  );
  const [showDialog, RemoveWorkflowDialog] = useDialog(ConfirmationDialog);

  const [formData, setFormData] = useState<PromptTestCaseFormData>({ ...testCase, run_count: 1 });
  const [isExecuteLoading, setIsExecuteLoading] = useState(false);
  const [localRuns, setLocalRuns] = useState<PromptTestCaseRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<PromptTestCasePreview>();
  const [lastPreviewFormData, setLastPreviewFormData] = useState<object | null>(null);
  const [lastPreviewPrompt, setLastPreviewPrompt] = useState<typeof prompt | null>(null);
  const [isOutputOpen, setIsOutputOpen] = useState(false);
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const customValidator = customizeValidator({ ajvOptionsOverrides: { useDefaults: true } });

  const runHistory = useMemo(() => {
    const list = runs ?? [];
    if (localRuns.length === 0) {
      return list;
    }

    const localRunIds = new Set(localRuns.map((run) => run.id));
    return [...localRuns, ...list.filter((run) => !localRunIds.has(run.id))];
  }, [runs, localRuns]);

  const latestRun = useMemo(() => {
    if (activeRunId) {
      return runHistory.find((run) => run.id === activeRunId) ?? null;
    }

    return runHistory[0] ?? null;
  }, [activeRunId, runHistory]);

  const getPersistedFormData = (nextFormData: PromptTestCaseFormData): PromptTestCaseRequest => {
    return {
      llm_model_id: nextFormData.llm_model_id,
      test_case: nextFormData.test_case,
      temperature: nextFormData.temperature,
    };
  };

  const handleOnChange = (formData: PromptTestCaseFormData) => {
    setFormData(formData);
  };

  const handleOnExecuteSubmit = () => {
    if (isExecuteLoading) {
      return;
    }

    setIsExecuteLoading(true);

    const persistedFormData = getPersistedFormData(formData);
    const runCount = Math.max(1, Math.floor(formData.run_count ?? 1));

    void update
      .mutateAsync(persistedFormData)
      .then(() => {
        return execute.mutateAsync({ run_count: runCount });
      })
      .then((results) => {
        setLocalRuns(results);
        setActiveRunId(results[0]?.id ?? null);
        setSelectedRunIds(runCount > 1 ? results.slice(0, 2).map((run) => run.id) : []);
      })
      .finally(() => {
        setIsExecuteLoading(false);
      });
  };

  const handlePreviewExpand = () => {
    if (previewData ?? getPreview.isPending) {
      return;
    }

    void getPreview.mutateAsync(getPersistedFormData(formData).test_case).then((results) => {
      setPreviewData(results);
      setLastPreviewFormData(formData.test_case);
      setLastPreviewPrompt(prompt);
    });
  };

  const handleRefreshPreview = () => {
    void getPreview.mutateAsync(getPersistedFormData(formData).test_case).then((results) => {
      setPreviewData(results);
      setLastPreviewFormData(formData.test_case);
      setLastPreviewPrompt(prompt);
    });
  };

  const isPreviewOutdated =
    previewData &&
    ((lastPreviewFormData && JSON.stringify(formData.test_case) !== JSON.stringify(lastPreviewFormData)) ??
      (lastPreviewPrompt && JSON.stringify(prompt) !== JSON.stringify(lastPreviewPrompt)));

  const formSchema = useCallback(() => {
    return getFormSchema(prompt?.inputs ?? [], models);
  }, [prompt, models]);

  const toggleRunSelection = (runId: string) => {
    setSelectedRunIds((previous) => {
      if (previous.includes(runId)) {
        return previous.filter((id) => id !== runId);
      }

      const next = [runId, ...previous];
      return next.slice(0, 2);
    });
  };

  const selectedRuns = useMemo(() => {
    return selectedRunIds
      .map((id) => runHistory.find((run) => run.id === id))
      .filter((run): run is PromptTestCaseRun => Boolean(run));
  }, [runHistory, selectedRunIds]);

  const modelLabelById = useMemo(() => {
    return new Map(models?.map((model) => [model.id, model.name]) ?? []);
  }, [models]);

  const formatRunTitle = (run: PromptTestCaseRun) => {
    const timestamp = new Date(run.created_at).toLocaleString();
    const modelLabel = modelLabelById.get(run.llm_model_id) ?? run.llm_model_id;
    return `${timestamp} • ${modelLabel}`;
  };

  if (isModelsLoading) return <Loader />;

  return (
    <TestCard>
      <Form
        templates={templates}
        schema={formSchema()}
        uiSchema={getUiSchema(prompt, models)}
        formData={formData}
        onChange={({ formData }) => handleOnChange(formData as PromptTestCaseFormData)}
        validator={customValidator}
        onSubmit={({ formData }) => update.mutate(getPersistedFormData(formData as PromptTestCaseFormData))}
      >
        <></>
      </Form>

      <TestActionBar>
        <RunButton
          variant="contained"
          startIcon={
            isExecuteLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PlayCircleOutlineIcon fontSize="small" />
            )
          }
          disabled={isExecuteLoading}
          onClick={handleOnExecuteSubmit}
        >
          Run test
        </RunButton>
        <ActionIconButton className="ml-auto" color="error" title="Remove test" onClick={() => showDialog()}>
          <DeleteIcon fontSize="small" />
        </ActionIconButton>
      </TestActionBar>

      {latestRun && (
        <ResultPanel>
          <div className="flex items-center justify-between">
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 700 }}>
              Latest output
            </Typography>
            <IconButton
              aria-label="expand output"
              size="small"
              title="Expand output"
              onClick={() => setIsOutputOpen(true)}
            >
              <OpenInFullIcon fontSize="inherit" />
            </IconButton>
          </div>
          <TextField
            aria-label="Latest output"
            multiline
            rows={4}
            inputProps={{ readOnly: true }}
            value={latestRun.response ?? ''}
            fullWidth
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <RunStat label="Total tokens" value={latestRun.token_usage_metadata.total_tokens} />
            <RunStat label="Prompt tokens" value={latestRun.token_usage_metadata.prompt_tokens} />
            <RunStat label="Completion tokens" value={latestRun.token_usage_metadata.completion_tokens} />
            <RunStat label="Total cost" value={`$${latestRun.token_usage_metadata.total_cost.toFixed(6)}`} />
          </div>
        </ResultPanel>
      )}

      <Dialog open={isOutputOpen && !!latestRun} onClose={() => setIsOutputOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold">Output</div>
            <CloseButton onClose={() => setIsOutputOpen(false)} />
          </div>
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            aria-label="Prompt output"
            aria-readonly={true}
            inputProps={{ readOnly: true }}
            value={latestRun?.response ?? ''}
            fullWidth
            minRows={12}
          />
        </DialogContent>
      </Dialog>

      <TestAccordion>
        <TestAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Run history
          </Typography>
        </TestAccordionSummary>
        <TestAccordionDetails>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="contained"
                onClick={() => setIsDiffOpen(true)}
                disabled={selectedRuns.length !== 2}
              >
                Compare
              </Button>
              <Button size="small" onClick={() => setSelectedRunIds([])} disabled={selectedRunIds.length === 0}>
                Clear selection
              </Button>
            </div>
            {areRunsLoading && (
              <div className="flex justify-center py-3">
                <CircularProgress size={20} />
              </div>
            )}
            {!areRunsLoading && runHistory.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No runs yet. Run the test to generate a run.
              </Typography>
            )}
            <div className="flex flex-col gap-1.5">
              {runHistory.map((run) => (
                <RunRow key={run.id}>
                  <Checkbox
                    size="small"
                    checked={selectedRunIds.includes(run.id)}
                    onChange={() => toggleRunSelection(run.id)}
                    inputProps={{
                      'aria-label': `Select run from ${new Date(run.created_at).toLocaleString()} using model ${
                        modelLabelById.get(run.llm_model_id) ?? run.llm_model_id
                      }`,
                    }}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{new Date(run.created_at).toLocaleString()}</span>
                    <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {modelLabelById.get(run.llm_model_id) ?? run.llm_model_id}
                    </span>
                  </div>
                  <Typography variant="caption" color="text.secondary" className="ml-auto shrink-0">
                    {run.token_usage_metadata.total_tokens} tok
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setActiveRunId(run.id)}>
                    View
                  </Button>
                </RunRow>
              ))}
            </div>
          </div>
        </TestAccordionDetails>
      </TestAccordion>

      <Dialog open={isDiffOpen} onClose={() => setIsDiffOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          <div className="flex items-center justify-between">
            <div className="text-xl font-bold">Output comparison</div>
            <CloseButton onClose={() => setIsDiffOpen(false)} />
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedRuns.length === 2 ? (
            <TextDiff
              left={selectedRuns[0].response ?? ''}
              right={selectedRuns[1].response ?? ''}
              leftTitle={formatRunTitle(selectedRuns[0])}
              rightTitle={formatRunTitle(selectedRuns[1])}
              maxHeight={640}
            />
          ) : (
            <Typography variant="body2">Select two runs to compare.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <TestAccordion onChange={(_, expanded) => expanded && handlePreviewExpand()}>
        <TestAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Prompt Preview
          </Typography>
        </TestAccordionSummary>
        <TestAccordionDetails>
          {getPreview.isPending && (
            <div className="flex justify-center py-4">
              <CircularProgress size={24} />
            </div>
          )}
          {previewData && !getPreview.isPending && (
            <div className="flex flex-col gap-3">
              {isPreviewOutdated && (
                <Alert severity="info">
                  Preview is outdated. The prompt template or test case inputs have changed since the last preview was
                  generated.
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefreshPreview}
                    disabled={getPreview.isPending}
                    sx={{ ml: 1 }}
                  >
                    Refresh Preview
                  </Button>
                </Alert>
              )}
              <TextField
                label="Generated Prompt"
                multiline
                rows={6}
                value={previewData.compiled_prompt}
                fullWidth
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
              <div className="flex justify-end">
                <Chip label={`${previewData.token_count} tokens`} color="primary" variant="outlined" size="small" />
              </div>
            </div>
          )}
        </TestAccordionDetails>
      </TestAccordion>

      <RemoveWorkflowDialog
        title={'Remove'}
        onConfirm={() => remove.mutate()}
        buttonsSettings={{ confirmText: 'Remove', confirmColor: 'error' }}
      >
        <Typography>Are you sure you want to remove this prompt test case?</Typography>
      </RemoveWorkflowDialog>
    </TestCard>
  );
};

export default PromptsTestCaseItem;
